import React, { useState } from "react";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  postDelete,
  postJobDelete,
  postList,
  postOrdering,
  postStatusChange,
} from "../../../../services/postService";
import Badge from "../../../../components/elements/Badge";
import { getPaginationRange, handleFilterChange } from "../../../../helpers";
import Table from "../../../../components/elements/table/Table";

import DeleteModal from "../../../../components/common/DeleteModal";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Tooltip from "../../../../components/elements/Tooltip";

import { status_filter } from "../../../../components/constant/constant";
import { useSearchParams } from "react-router-dom";
import { useSocket } from "../../../../providers/SocketContext";
import { setTitle } from "../../../../helpers/MetaTag";
import { useDebounce } from "../../../../hooks/useDebounce";

const JobDashboard = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const userLang = localStorage.getItem("lang") || "en";
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("organization");

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const statusArray = [
    {
      state: "active",
      color: "success",
    },
    {
      state: "inactive",
      color: "danger",
    },
    {
      state: "draft",
      color: "gold",
    },
    {
      state: "private",
      color: "default",
    },
  ];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["standard-job", filters, search],
    queryFn: async () => {
      const res = await postList({
        ...filters,

        ...(search && { keyword: search }),
        ...(organizationId && { organization: organizationId }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });
  useSocket("jobManageResponse", (job) => {
    if (!job?._id) return;

    queryClient.setQueryData(["standard-job", filters, search], (oldData) => {
      if (!oldData) return oldData;

      const existingData = oldData?.data || [];

      const existsIndex = existingData.findIndex(
        (item) => item?._id === job?._id,
      );

      let updatedList = [...existingData];

      // 🆕 If NOT exists → INSERT (new job)
      if (existsIndex === -1) {
        // If user is not on page 1, just refetch instead
        if (filters.page !== 1) {
          queryClient.invalidateQueries(["standard-job"]);
          return oldData;
        }

        updatedList = [job, ...existingData];

        return {
          ...oldData,
          data: updatedList,
          metadata: {
            ...oldData.metadata,
            totalDocs: (oldData.metadata?.totalDocs || 0) + 1,
          },
        };
      }

      // ✏️ If exists → UPDATE
      updatedList[existsIndex] = {
        ...existingData[existsIndex],
        ...job,
      };

      return {
        ...oldData,
        data: updatedList,
      };
    });
  });

  //   Jobs status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        queryClient.setQueryData(["standard-job", filters, search], (state) => {
          if (!state) return;

          const updatedData = state?.data?.map((item) =>
            item?._id === id
              ? {
                  ...item,
                  status: item.status === "active" ? "inactive" : "active",
                }
              : item,
          );

          return { ...state, data: updatedData };
        });
        const res = await postStatusChange({
          ids: [id],
          status: status === "active" ? "inactive" : "active",
        });

        return { res, payload: { id, status } };
      },

      onSuccess: ({ res, payload }) => {
        if (res?.status === 200) {
          toast.success("Job status change successfully.");
        } else {
          queryClient.setQueryData(
            ["standard-job", filters, search],
            (state) => {
              if (!state) return;

              const revertedData = state?.data?.map((item) =>
                item?._id === payload.id
                  ? {
                      ...item,
                      status: item.status === "active" ? "inactive" : "active",
                    }
                  : item,
              );

              return { ...state, data: revertedData };
            },
          );

          toast.error(res?.message || "Failed to update status.");
        }
      },

      onError: (error) => {
        toast.error(
          error?.message || "Something went wrong while updating status.",
        );
        console.error("Status update error:", error);
      },
    });

  const tableHeadData = [
    // {
    //   _id: 1,
    //   name: "Image",
    //   align: "left",
    // },
    {
      _id: 2,
      name: "Title",
      align: "left",
      isSort: false,
      // isFilter: false,
      // isFilterSearch: false,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-title" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "title" }));
        }
      },
    },
    {
      _id: 3,
      name: "Skills",
      align: "left",
      // isSort: false,
      // isFilter: false,
      // isFilterSearch: false,
    },
    {
      _id: 4,
      name: "Job Type",
      align: "left",
      // isSort: false,
      // isFilter: false,
      // isFilterSearch: false,
    },
    {
      _id: 5,
      name: "Location Type",
      align: "left",
      // isSort: false,
      // isFilter: false,
      // isFilterSearch: false,
    },
    {
      _id: 6,
      name: "Salary",
      align: "left",
      // isSort: false,
      // isFilter: false,
      // isFilterSearch: false,
    },
    {
      _id: 7,
      name: "Publish Date",
      align: "left",
      isSort: false,
      onSort: (val) => {
        if (val === "DESC") {
          setFilters((pre) => ({ ...pre, sortQuery: "-createdAt" }));
        } else {
          setFilters((pre) => ({ ...pre, sortQuery: "createdAt" }));
        }
      },
    },
    {
      _id: 8,
      name: "Status",
      align: "left",
      isFilter: false,
      isFilterSearch: false,
      onFilter: (val) => {
        if (val?.length > 0 && val?.length === 1) {
          setFilters((pre) => ({
            ...pre,
            status: val?.map((item) => item?.value),
            page: 1,
          }));
        } else {
          setFilters((pre) => {
            delete pre?.status;
            return { ...pre };
          });
        }
      },
      filterData: status_filter || [],
    },
    // {
    //   _id: 9,
    //   name: "Action",
    //   align: "left",
    // },
  ];

  const tagColors = [
    "bg-purple-100 text-purple-700 border border-purple-200",
    "bg-lime-100 text-lime-700 border border-lime-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
    "bg-blue-100 text-blue-700 border border-blue-200",
    "bg-purple-100 text-purple-700 border border-purple-200",
    "bg-orange-100 text-orange-700 border border-orange-200",
    "bg-pink-100 text-pink-700 border border-pink-200",
    "bg-indigo-100 text-indigo-700 border border-indigo-200",
    "bg-teal-100 text-teal-700 border border-teal-200",
    "bg-yellow-100 text-yellow-700 border border-yellow-200",
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "user",
          align: "left",
          cellClass: "!min-w-[100px]",
          image: item?.coverMedia?.url,
          data: item?.content?.[userLang]?.title || "-",
        },
        // {
        //   _id: 2,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: <div dangerouslySetInnerHTML={{ __html: item?.content?.[userLang]?.title || "-" }}></div> || "-",
        // },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <>
              {item?.job?.skills.length > 1 ? (
                <Tooltip
                  position="bottom"
                  className=""
                  message={
                    <div className="text-[12px] whitespace-pre-wrap max-w-xs flex flex-wrap gap-1">
                      {item?.job?.skills.map((it, ind) => (
                        <Badge
                          key={ind}
                          customColor={tagColors[ind % tagColors.length]}
                          label={it?.content?.[userLang]?.title}
                          className="!rounded-full !h-6"
                        />
                      ))}
                    </div>
                  }
                  messageClass="!bg-gradient-to-br !from-slate-600 !via-slate-500 !to-slate-500 !p-2 !rounded-lg !shadow-md border !border-gray-200"
                >
                  <div className="flex items-center gap-2">
                    <Badge
                      customColor={tagColors[0]}
                      label={item?.job?.skills[0]?.content?.[userLang]?.title}
                      className="!rounded-full !h-6"
                    />
                    <span className="bg-gray-100 text-gray-800 font-bold text-xs px-2 py-1 rounded-full border border-gray-300">
                      +{item?.job?.skills.length - 1} more
                    </span>
                  </div>
                </Tooltip>
              ) : (
                <div className="flex items-center gap-2">
                  {item?.job?.skills.length === 1 ? (
                    <Badge
                      customColor={tagColors[0]}
                      label={item?.job?.skills[0]?.content?.[userLang]?.title}
                      className="!rounded-full !h-6"
                    />
                  ) : (
                    <span className="text-gray-500 text-sm text-center">-</span>
                  )}
                </div>
              )}
            </>
          ),
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item.job.experience?.content[userLang]?.title || "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.locationType || "-",
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.job?.budget?.range
            ? `${item?.job?.budget?.range?.unit === "usd" ? "$" : "₹"}${item?.job?.budget?.range?.min >= 1000 ? item?.job?.budget?.range?.min / 1000 + "k" : item?.job?.budget?.range?.min}  -   ${item?.job?.budget?.range?.unit === "usd" ? "$" : "₹"}${
                item?.job?.budget?.range?.max >= 1000
                  ? item?.job?.budget?.range?.max / 1000 + "k"
                  : item?.job?.budget?.range?.max
              }`
            : "-",
        },
        {
          _id: 7,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.createdAt
            ? dayjs(item?.createdAt).format("DD MMM, YYYY")
            : "-",
        },
        {
          _id: 8,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div className="flex gap-2 items-center ">
              <Badge
                type={statusArray.find((st) => st.state === item?.status).color}
                label={
                  statusArray.find((st) => st.state === item?.status).state
                }
              />
            </div>
          ),
        },
        // {
        //   _id: 9,
        //   type: "action",
        //   align: "left",
        //   cellClass: "w-[5%]",
        //   actionData: [
        //     {
        //       _id: 1,
        //       name: "Application",
        //       icon: "fa-regular fa-paper-plane",
        //       onClick: () => {
        //         navigate(`/admin/job-applications?jobId=${item?._id}`);
        //       },
        //       standout: false,
        //     },
        //     {
        //       _id: 2,
        //       name: "Details",
        //       icon: "fa-regular fa-circle-info",
        //       onClick: () => {
        //         navigate(`/admin/job-details/${item?.slug}`);
        //       },
        //       standout: false,
        //     },
        //     {
        //       _id: 3,
        //       name: "Edit",
        //       icon: "fa-regular fa-pen-to-square",
        //       onClick: () => {
        //         navigate(`/admin/manage-jobs/${item?.slug}`);
        //       },
        //       standout: false,
        //     },

        //     {
        //       _id: 4,
        //       name: "Delete",
        //       icon: "fa-regular fa-trash-can",
        //       standout: true,
        //       onClick: () => {
        //         setDeleteModal({
        //           isOpen: true,
        //           data: item,
        //         });
        //       },
        //     },
        //   ],
        // },
      ],
    };
  });

  //   Jobs delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await postJobDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["standard-job", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter(
              (item) => !payload?.includes(item?._id),
            );
            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["standard-job", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  async function postOrderingHandler(newData, oldData) {
    const ordering_data = newData?.map((item, i) => ({
      id: item?._id,
      ordering: i,
      categoryType: "standard-job",
    }));
    const res = await postOrdering({ ordering_data });
    if (res?.status !== 200) {
      queryClient.setQueryData(["standard-job", filters, search], (state) => {
        if (state) {
          return { ...state, data: oldData };
        }
      });
      toast.warning(res?.message || res?.error);
    }
  }
  const options = [
    {
      id: "jobsDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Jobs Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "jobsDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }
  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="text-lg xls:text-3xl text-bunker font-semibold mt-5">
        Latest Jobs
      </div>
      {/* <div className="space-y-8">
        <div className="flex items-center flex-wrap sm:flex-nowrap gap-3">
          <TableControls
            limit={filters?.limit}
            totalDocs={data?.metadata?.totalDocs}
            onLimitChange={(val) =>
              handleFilterChange("limit", val, setFilters)
            }
            onSearch={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-md bg-secondary text-surface-01 text-xs px-1 py-1 gap-1">
                <span className="inline-block px-1">
                  {selectedItems?.length} Selected
                </span>
                <button
                  onClick={() => setSelectedItems([])}
                  type="button"
                  className="cursor-pointer"
                >
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
          <div className="relative ml-auto">
            <Button
              buttonIcon={"fa-regular fa-plus"}
              buttonLabel={"Create New"}
              buttonFunction={() => navigate("/admin/manage-jobs")}
            />
          </div>
        </div>
      </div> */}

      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        // pageCount={data?.metadata?.totalPages}
        // currentPage={filters?.page}
        // onPageChange={(val) => {
        //   handleFilterChange("page", val, setFilters);
        //   setSelectedItems([]);
        // }}
        // handleOrdering={(newData, oldData) => {
        //   postOrderingHandler(newData, oldData);
        // }}
        // originalData={[...(data?.data || [])]}
        // setOriginalData={(data) =>
        //   queryClient.setQueryData(
        //     ["standard-job", filters, search],
        //     (state) => {
        //       if (state) {
        //         return { ...state, data: data };
        //       }
        //     },
        //   )
        // }
        isFetching={isFetching}
        // selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        // showingData={getPaginationRange(
        //   filters?.page,
        //   filters?.limit,
        //   data?.metadata?.totalDocs,
        // )}
        // selectable
      />

      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default JobDashboard;
